<script setup lang="ts">
import { computed, ref } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import type { TColors } from '@/services/application/interfaces'
import { bgColors } from '@/services/application/constantes'
import BButton from '@/components/common/bButton.vue'
import { useI18n } from 'vue-i18n'
import IconClose from '~icons/ic/round-close'
import MaterialSymbolsCheckCircleOutlineRounded from '~icons/material-symbols/check-circle-outline-rounded'
import MaterialSymbolsWarningOutlineRounded from '~icons/material-symbols/warning-outline-rounded'
import MaterialSymbolsErrorOutlineRounded from '~icons/material-symbols/error-outline-rounded'
import MaterialSymbolsInfoOutlineRounded from '~icons/material-symbols/info-outline-rounded'

const { t } = useI18n()

const isPaused = ref(false)
const isStopped = ref(false)
const remainingTime = ref(0)
const progressWidth = ref(100)
const isUndoLoading = ref(false)

const props = withDefaults(defineProps<{
  alert: IAlert
  shadow?: boolean
  size?: keyof typeof sizes
  showClose?: boolean
}>(), {
  shadow: false,
  size: 'large',
  showClose: true,
})

const emits = defineEmits<{
  'close': [alert: IAlert]
}>()

const sizes = {
  small: 'px-3 py-2 gap-3',
  normal: 'px-4 py-3 gap-3',
  large: 'px-5 py-4 gap-4',
}

const sizesTextTitle = {
  small: 'text-sm',
  normal: 'text-base',
  large: 'text-lg',
}

type IAlert = {
  id?: string
  title?: string
  message?: string
  color?: keyof typeof bgColors
  duration: number
  includeIcon?: boolean
  version?: number
  undoCallback?: () => void | Promise<void>
  undoTo?: RouteLocationRaw
  undoLabel?: string
}

const hasUndoAction = computed(() => Boolean(props.alert.undoCallback || props.alert.undoTo))

const close = () => {
  emits('close', props.alert)
}

const handleUndo = async () => {
  if (props.alert.undoTo) {
    close()
    return
  }
  if (!props.alert.undoCallback || isUndoLoading.value) return

  isUndoLoading.value = true
  try {
    await props.alert.undoCallback()
    close()
  } finally {
    isUndoLoading.value = false
  }
}

const getIcon = (color?: string) => {
  if (!color) return MaterialSymbolsInfoOutlineRounded
  if (color.includes('success')) return MaterialSymbolsCheckCircleOutlineRounded
  if (color.includes('warning')) return MaterialSymbolsWarningOutlineRounded
  if (color.includes('error') || color.includes('danger')) return MaterialSymbolsErrorOutlineRounded
  return MaterialSymbolsInfoOutlineRounded
}

const accentColors: Record<string, string> = {
  'primary': 'text-primary-500',
  'primary-op': 'text-primary-500',
  'success': 'text-success-500',
  'success-op': 'text-success-500',
  'warning': 'text-warning-500',
  'warning-op': 'text-warning-500',
  'error': 'text-red-500',
  'error-op': 'text-red-500',
  'errorLight': 'text-red-400',
  'errorLight-op': 'text-red-400',
  'blue-op': 'text-blue-500',
  'purple-op': 'text-purple-500',
  'orange-op': 'text-orange-500',
}

const accentBgColors: Record<string, string> = {
  'primary': 'bg-primary-500',
  'primary-op': 'bg-primary-500',
  'success': 'bg-success-500',
  'success-op': 'bg-success-500',
  'warning': 'bg-warning-500',
  'warning-op': 'bg-warning-500',
  'error': 'bg-red-500',
  'error-op': 'bg-red-500',
  'errorLight': 'bg-red-400',
  'errorLight-op': 'bg-red-400',
  'blue-op': 'bg-blue-500',
  'purple-op': 'bg-purple-500',
  'orange-op': 'bg-orange-500',
}

const accentBorderColors: Record<string, string> = {
  'primary': 'border-l-primary-500',
  'primary-op': 'border-l-primary-500',
  'success': 'border-l-success-500',
  'success-op': 'border-l-success-500',
  'warning': 'border-l-warning-500',
  'warning-op': 'border-l-warning-500',
  'error': 'border-l-red-500',
  'error-op': 'border-l-red-500',
  'errorLight': 'border-l-red-400',
  'errorLight-op': 'border-l-red-400',
  'blue-op': 'border-l-blue-500',
  'purple-op': 'border-l-purple-500',
  'orange-op': 'border-l-orange-500',
}

const radialGradients: Record<string, string> = {
  'primary': 'radial-primary',
  'primary-op': 'radial-primary',
  'success': 'radial-success',
  'success-op': 'radial-success',
  'warning': 'radial-warning',
  'warning-op': 'radial-warning',
  'error': 'radial-error',
  'error-op': 'radial-error',
  'errorLight': 'radial-error',
  'errorLight-op': 'radial-error',
  'blue-op': 'radial-blue',
  'purple-op': 'radial-purple',
  'orange-op': 'radial-orange',
}

// Animation logic
let animationFrame: number | null = null
let startTime: number | null = null
let pausedAt: number | null = null

const animate = (timestamp: number) => {
  if (!startTime) startTime = timestamp
  
  const elapsed = timestamp - startTime
  const duration = props.alert.duration
  const progress = Math.min(elapsed / duration, 1)
  
  progressWidth.value = (1 - progress) * 100
  remainingTime.value = Math.ceil((duration - elapsed) / 1000)
  
  if (progress >= 1) {
    close()
    return
  }
  
  if (!isPaused.value && !isStopped.value) {
    animationFrame = requestAnimationFrame(animate)
  }
}

const startAnimation = () => {
  if (props.alert.duration >= 999999 || props.alert.duration <= 0) return
  remainingTime.value = Math.ceil(props.alert.duration / 1000)
  animationFrame = requestAnimationFrame(animate)
}

const pauseAnimation = () => {
  if (isStopped.value || props.alert.duration >= 999999 || props.alert.duration <= 0) return
  isPaused.value = true
  pausedAt = performance.now()
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
    animationFrame = null
  }
}

const resumeAnimation = () => {
  if (isStopped.value || props.alert.duration >= 999999 || props.alert.duration <= 0) return
  isPaused.value = false
  if (pausedAt && startTime) {
    startTime += performance.now() - pausedAt
    pausedAt = null
  }
  animationFrame = requestAnimationFrame(animate)
}

const stopPermanently = () => {
  isStopped.value = true
  isPaused.value = false
  if (animationFrame) {
    cancelAnimationFrame(animationFrame)
    animationFrame = null
  }
}

// Start animation on mount
startAnimation()
</script>

<template>
  <div 
    class="rounded-xl relative transition-all flex items-start overflow-hidden border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900 border-l-[3px]" 
    :class="[{ 'shadow-xl': shadow }, sizes[size], accentBorderColors[alert.color || 'primary-op']]"
    @mouseenter="pauseAnimation"
    @mouseleave="resumeAnimation"
  >
    <!-- Radial gradient glow effect -->
    <div 
      class="absolute inset-0 pointer-events-none opacity-30 dark:opacity-20" 
      :class="[radialGradients[alert.color || 'primary-op']]"
    />

    <!-- Icon -->
    <div :class="[accentColors[alert.color || 'primary-op'] || 'text-primary-500']" class="shrink-0 relative z-10">
      <slot name="icon" :alert="alert">
        <component :is="getIcon(alert.color)" class="w-6 h-6" />
      </slot>
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0 relative z-10">
      <slot name="header" :alert="alert" v-if="alert.title">
        <header class="font-semibold text-gray-900 dark:text-white" :class="[sizesTextTitle[size]]">
          <slot name="header-content" :alert="alert">
            <h4>{{ alert.title }}</h4>
          </slot>
        </header>
      </slot>
      
      <slot name="content" :alert="alert">
        <p v-if="alert.message" class="text-sm text-gray-600 dark:text-gray-400 mt-0.5 whitespace-pre-line">
          {{ alert.message }}
        </p>
      </slot>

      <!-- Actions Slot (e.g. Undo button / router link) -->
      <div v-if="hasUndoAction" class="mt-3">
        <BButton
          size="dense"
          bg="base"
          :to="alert.undoTo"
          :is-loading="isUndoLoading"
          @click="handleUndo"
        >
          {{ alert.undoLabel || t('components.confirmationModal.cancel') }}
        </BButton>
      </div>
      <div v-else-if="$slots.actions" class="mt-3">
        <slot name="actions" :alert="alert"></slot>
      </div>

      <!-- Click to stop text -->
      <div 
        v-if="isPaused && !isStopped && alert.duration < 999999 && alert.duration > 0" 
        class="text-sm mt-3 cursor-pointer" 
        :class="[accentColors[alert.color || 'primary-op'] || 'text-primary-500']"
        @click="stopPermanently"
      >
        This message will close in {{ remainingTime }} seconds. <span class="font-semibold text-gray-900 dark:text-white">Click to stop.</span>
      </div>
    </div>

    <!-- Close Button -->
    <slot v-if="showClose" name="close-button" :alert="alert" :close="close">
      <button class="shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-white transition-colors relative z-10" @click="close">
        <slot name="icon-close">
          <IconClose class="w-5 h-5" />
        </slot>
      </button>
    </slot>

    <!-- Progress Bar -->
    <div 
      v-if="alert.duration < 999999 && alert.duration > 0 && !isStopped" 
      class="absolute h-0.5 left-0 bottom-0 transition-none z-10" 
      :class="[accentBgColors[alert.color || 'primary-op'] || 'bg-primary-500']"
      :style="{ width: `${progressWidth}%` }"
    />
  </div>
</template>

<style scoped>
.radial-primary {
  background: radial-gradient(circle at left center, color-mix(in srgb, var(--color-primary-500) 30%, transparent) 0%, transparent 50%);
}
.radial-success {
  background: radial-gradient(circle at left center, color-mix(in srgb, var(--color-success-500) 30%, transparent) 0%, transparent 50%);
}
.radial-warning {
  background: radial-gradient(circle at left center, rgb(251 191 36 / 0.3) 0%, transparent 50%);
}
.radial-error {
  background: radial-gradient(circle at left center, rgb(239 68 68 / 0.3) 0%, transparent 50%);
}
.radial-blue {
  background: radial-gradient(circle at left center, rgb(59 130 246 / 0.3) 0%, transparent 50%);
}
.radial-purple {
  background: radial-gradient(circle at left center, rgb(168 85 247 / 0.3) 0%, transparent 50%);
}
.radial-orange {
  background: radial-gradient(circle at left center, rgb(249 115 22 / 0.3) 0%, transparent 50%);
}
</style>
